import { useCallback, useEffect, useState } from "react";
import { setUser as setSentryUser } from "@sentry/react";

import { getUser, setUser, logout as apiLogout } from "api/cookie_auth";
import type { User } from "types";

export type AuthState = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
};

export interface UseAuthReturn extends AuthState {
  logout: () => Promise<void>;
  refreshAuthState: () => void;
}

const USER_STORAGE_KEY = "session_user";

export const useAuth = (): UseAuthReturn => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const refreshAuthState = useCallback(() => {
    const user = getUser();
    const authenticated = Boolean(user);

    if (user) {
      setSentryUser({ id: String(user.id), email: user.email, username: user.username });
    } else {
      setSentryUser(null);
    }

    setAuthState({
      user,
      isAuthenticated: authenticated,
      isLoading: false,
    });
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
    setSentryUser(null);
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    refreshAuthState();
  }, [refreshAuthState]);

  // Listen for storage changes (in case of logout from another tab)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === USER_STORAGE_KEY) {
        refreshAuthState();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [refreshAuthState]);

  return {
    ...authState,
    logout,
    refreshAuthState,
  };
};
